import { useState } from "react";
import { Megaphone, Check, X, Trash2, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { mediaUrl, listingPriceParts } from "@/lib/api";
import ListingEditModal from "@/components/ListingEditModal";

const IMG_FALLBACK = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=200";

function statusBadgeClass(status) {
  if (status === "approved") return "bg-green-600";
  if (status === "rejected") return "bg-red-600";
  if (status === "sold") return "bg-slate-500";
  return "bg-amber-500";
}

function statusLabel(status) {
  if (status === "approved") return "Aprobat";
  if (status === "rejected") return "Respins";
  if (status === "sold") return "Vândut";
  return "În așteptare";
}

export default function AdminListings({ listings, pendingCount, catName, onApprove, onReject, onDelete, onSaved }) {
  const [editing, setEditing] = useState(null);
  return (
    <>
      <h2 className="font-bold text-lg mb-5">Anunțuri utilizatori ({listings.length}) · {pendingCount} în așteptare</h2>
      <div className="space-y-3">
        {listings.length === 0 && <div className="border border-slate-200 p-8 text-center text-slate-400">Niciun anunț.</div>}
        {listings.map((l) => {
          const active = l.status !== "sold";
          return (
            <div key={l.id} className="border border-slate-200 p-4 flex flex-col sm:flex-row gap-4" data-testid={`admin-listing-${l.id}`}>
              <img src={mediaUrl(l.image) || IMG_FALLBACK} alt={l.name} className="w-full sm:w-24 h-24 object-cover bg-slate-100 border border-slate-200" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold flex items-center gap-2"><Megaphone className="w-4 h-4 text-orange-600 shrink-0" /> {l.name}</div>
                  <Badge className={`rounded-none ${statusBadgeClass(l.status)}`}>{statusLabel(l.status)}</Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1">{l.seller_name} · {l.seller_email}{l.seller_phone ? ` · ${l.seller_phone}` : ""} · {catName(l.category)}</div>
                <div className="text-sm font-black mt-1">{(() => { const p = listingPriceParts(l); return (<>{p.main}{p.eur ? <span className="font-normal text-slate-400 font-mono ml-2">{p.eur}</span> : null}</>); })()} {l.location && <span className="font-normal text-slate-400">· {l.location}</span>}</div>
                {l.main_parameters && <div className="text-sm text-slate-600 mt-1"><span className="font-semibold">Parametri:</span> {l.main_parameters}</div>}
                {l.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{l.description}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => setEditing(l)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-orange-600 hover:text-orange-600 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`edit-listing-${l.id}`}><Pencil className="w-3.5 h-3.5" /> Editează</button>
                  {active && l.status !== "approved" && (
                    <button onClick={() => onApprove(l.id)} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`approve-${l.id}`}><Check className="w-3.5 h-3.5" /> Aprobă</button>
                  )}
                  {active && l.status !== "rejected" && (
                    <button onClick={() => onReject(l.id)} className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`reject-${l.id}`}><X className="w-3.5 h-3.5" /> Respinge</button>
                  )}
                  <button onClick={() => onDelete(l.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase transition-colors" data-testid={`delete-listing-${l.id}`}><Trash2 className="w-3.5 h-3.5" /> Șterge</button>
                </div>
                {Array.isArray(l.edit_history) && l.edit_history.length > 0 && (
                  <details className="mt-3 text-xs" data-testid={`edit-history-${l.id}`}>
                    <summary className="cursor-pointer font-bold text-slate-500 uppercase tracking-wide">Istoric modificări ({l.edit_history.length})</summary>
                    <ul className="mt-2 space-y-1 border-l-2 border-slate-200 pl-3">
                      {[...l.edit_history].reverse().map((h, i) => (
                        <li key={i} className="text-slate-600">
                          <span className="font-mono text-slate-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                          {" · "}<span className="font-semibold">{h.by_email || "?"}</span>
                          <span className="text-slate-400"> ({h.by_role})</span>
                          {" — "}{(h.fields || []).join(", ")}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {editing && (
        <ListingEditModal listing={editing} isAdmin onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </>
  );
}
